/**
 * The menu behind Escape.
 *
 * Everything about the session rather than about the world: the tools, the
 * settings, and the way out. They used to sit along the top of the screen, where
 * they were three things a player never presses taking up the corner the world
 * was in.
 *
 * Escape opens and closes it, and so does the button it left behind.
 */

import { useEffect } from 'react';
import { CloseButton } from './CloseButton';

export function SystemMenu({
  canUseTools,
  canEditWorld,
  toolsOpen,
  editing,
  onTools,
  onEditing,
  onBoards,
  onCollection,
  onExchange,
  onSettings,
  onLogOut,
  onClose,
}: {
  /** Whether this account has any business with the tools at all. */
  canUseTools: boolean;
  /** Whether it may take the world over as well, which is a rank further up. */
  canEditWorld: boolean;
  toolsOpen: boolean;
  editing: boolean;
  /** Opens the tools, on a named section where one is asked for. */
  onTools: (section?: 'players' | 'server' | 'look') => void;
  onEditing: (open: boolean) => void;
  onBoards: () => void;
  onCollection: () => void;
  onExchange: () => void;
  onSettings: () => void;
  onLogOut: () => void;
  onClose: () => void;
}): JSX.Element {
  // Escape closes it again, wherever the pointer is.
  useEffect(() => {
    const key = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [onClose]);

  return (
    <div className="system-menu">
      <div className="panel-card">
        <div className="panel-head">
          <span>Menu</span>
          <CloseButton onClose={onClose} />
        </div>

        <div className="panel-body">
          <button type="button" className="wide primary" onClick={onSettings}>
            Settings
          </button>

          <button type="button" className="wide" onClick={onExchange}>
            Exchange
          </button>

          <button type="button" className="wide" onClick={onCollection}>
            Collection log
          </button>

          <button type="button" className="wide" onClick={onBoards}>
            Leaderboards
          </button>

          <button type="button" className="wide danger" onClick={onLogOut}>
            Log out
          </button>

          {/* Underneath the two things everybody has, because that is what they
              are: everything below is for the handful of accounts that hold a
              rank, and it belongs after the ordinary way out rather than in
              front of it. */}
          {canUseTools ? (
            <>
              <h3>Admin Tools</h3>

              <button type="button" className="wide" onClick={() => onTools('players')}>
                Players
              </button>

              {canEditWorld ? (
                <button type="button" className="wide" onClick={() => onTools('server')}>
                  Server
                </button>
              ) : null}

              <button type="button" className="wide" onClick={() => onTools('look')}>
                Appearance
              </button>

              {canEditWorld ? (
                <button
                  type="button"
                  className={editing ? 'wide on' : 'wide'}
                  onClick={() => {
                    onEditing(!editing);
                    onClose();
                  }}
                >
                  {editing ? 'Close the world editor' : 'World Editor'}
                </button>
              ) : null}

              {toolsOpen ? (
                <button type="button" className="wide" onClick={() => onTools()}>
                  Put the tools away
                </button>
              ) : null}
            </>
          ) : null}

          <p className="hint">Escape closes this, and opens it again.</p>
        </div>
      </div>
    </div>
  );
}
